import { acceptHMRUpdate, defineStore } from 'pinia';
import app from '@/main'
import MapDistanceControl from '@/views/controls/MapDistanceControl.vue'
import { toRaw } from 'vue'
import { createVNode, render } from 'vue'
import { useMaps } from './maps'
import * as turf from '@turf/turf'
import { useDrawingStore } from './drawing'
import { useImperialUnitsStore } from './imperialUnits'
import { usePlanner } from './planner'
import { useEditPathStore } from './editPath'
import { useEasyModeStore } from './easyMode'
import { useParkingLotsStore } from './parkingLots'
import { useVehiclesLiveStore } from './vehiclesLive'
import { useAsAppliedMap } from './asAppliedMap'
import { useYieldMap } from './yieldMap'
import { useIsoPreview } from './isoPreview'

// Stores whose map click listeners must be suspended during measuring.
// Add any future store here and the pause and resume methods in the store
// no other changes needed. Beware of circular dependencies
const MAP_CLICK_LISTENER_STORES = [
    () => useEditPathStore(),
    () => useEasyModeStore(),
    () => useParkingLotsStore()
]

export const useMeasure = defineStore('measuring', {
    state: () => ({
        controlMeasure: null,
        measure: {
            measuring: false,
            measureMode: null,
            measureStart: null,
            measureLines: [],
            measureLabels: [],
            lastMousePosition: null,
            measurePolygon: null,
            measurePolygonPath: [],
            buttonContainer: null
        },
        mapClickListener: null,
        measureBtnState: null,
        measureListeners: [],
        disabledUnclickablePolygons: [],
        disabledUnclickableFields: []
    }),
    getters: {
        mapStore() {
            return useMaps()
        },
        google() {
            return this.mapStore.google
        },
        map() {
            return this.mapStore.map
        },
        drawingStore() {
            return useDrawingStore()
        },
        plannerStore() {
            return usePlanner()
        }
    },
    actions: {
        addMapDistanceControl() {
            // Check if the control already exists
            if (this.controlMeasure) {
                this.removeMapDistanceControl()
            }

            // // Create a container for the Vue component
            const controlMeasureDiv = document.createElement('div')
            this.controlMeasure = controlMeasureDiv
            const vnode = createVNode(MapDistanceControl, {
                map: this.map,
                onStartMeasuring: (mode, container) => this.startMeasuring(mode, container)
            })
            vnode.appContext = app._context
            render(vnode, this.controlMeasure)

            this.mapStore.controlContainer.prepend(this.controlMeasure)
        },
        removeMapDistanceControl () {
            render(null, this.controlMeasure)
            this.controlMeasure.remove()
            this.controlMeasure = null
        },
        startMeasuring(mode, buttonContainer) {
            // Temporarily suspend map click listeners from other stores
            MAP_CLICK_LISTENER_STORES.forEach(getStore => getStore().pauseMapClickListener())

            // Initialize measurement mode and set up UI elements
            this.initializeMeasurement(mode)
            this.setupStopButton(buttonContainer)
            this.setupMeasurementMode(mode)
            this.buttonContainer = buttonContainer
            this.disableDrawedField()
            this.setFieldsNotClickable()
            this.setUncoveredPolygonsUnclickable()
        },
        mapOverlayBlocksFieldClicks() {
            const aam = useAsAppliedMap()
            const yieldMap = useYieldMap()
            const isoPreview = useIsoPreview()
            const vLive = useVehiclesLiveStore()
            return aam.visible
                || yieldMap.yieldHeatmapVisible
                || isoPreview.visible
                || !!vLive.visibleOnMapMachineId
        },
        syncMapOverlaysAfterMeasure() {
            const vLive = useVehiclesLiveStore()
            if (vLive.visibleOnMapMachineId) {
                this.mapStore.hideFieldFills()
            }
        },
        setFieldsNotClickable() {
            this.mapStore.fields.forEach((field) => {
                if (field.poly.get('clickable') !== false) {
                    field.poly.setOptions({ clickable: false })
                    this.disabledUnclickableFields.push(field)
                }
            })
        },
        setFieldsClickable() {
            if (this.mapOverlayBlocksFieldClicks()) return
            if (this.plannerStore.routeData.field_id) return

            this.disabledUnclickableFields.forEach((field) => {
                if (!field.poly || field.fillHidden) {
                    return
                }
                field.poly.setOptions({ clickable: true })
            })
            this.disabledUnclickableFields = []
        },
        setUncoveredPolygonsUnclickable() {
            this.disabledUnclickablePolygons = []
            this.plannerStore.uncoveredPolygons.forEach((polygon) => {
                if (polygon.get('clickable') !== false) {
                    this.disabledUnclickablePolygons.push(polygon)
                    polygon.setOptions({ clickable: false })
                }
            })
        },
        setUncoveredPolygonsClickable() {
            this.disabledUnclickablePolygons.forEach((polygon) => {
                polygon.setOptions({ clickable: true })
            })
            this.disabledUnclickablePolygons = []
        },
        initializeMeasurement(mode) {
            // Set the measuring state and selected mode
            this.measure.measuring = true
            this.measure.measureMode = mode
        },
        setupStopButton() {
            this.measureBtnState = 'cancel'
            MapDistanceControl.measureButtonText

            this.mapClickListener = this.google.maps.event.addListener(this.map, 'click', () => {
                this.measureBtnState = 'stop'
                MapDistanceControl.measureButtonText
            })
        },
        setupMeasurementMode(mode) {
            // Change cursor to crosshair to indicate measuring mode
            this.map.setOptions({ draggableCursor: 'crosshair'})

            // Set up event listeners based on selected measurement mode
            if (mode === 'distance') {
                this.setupDistanceMeasurement()
            } else if (mode === 'area') {
                this.setupAreaMeasurement()
            }
        },
        setupDistanceMeasurement() {
            // Add event listeners for measuring distance
            this.measureListeners.push(
                this.google.maps.event.addListener(this.map, 'click', this.startMeasure),
                this.google.maps.event.addListener(this.map, 'mousemove', this.updateMeasure)
            )
        },
        setupAreaMeasurement() {
            // Add event listeners for measuring area
            this.measureListeners.push(
                this.google.maps.event.addListener(this.map, 'click', this.addAreaPoint),
                this.google.maps.event.addListener(this.map, 'dblclick', this.addAreaPoint),
                this.google.maps.event.addListener(this.map, 'mousemove', this.updateAreaPreview)
            )
            // Initialize an empty polygon for area measurement
            this.measure.measurePolygonPath = []
            this.measure.measurePolygon = new this.google.maps.Polygon({
                paths: [],
                strokeColor: '#000000',
                strokeOpacity: 1.0,
                strokeWeight: 2,
                fillColor: '#000000',
                fillOpacity: 0.3,
                map: toRaw(this.map),
                clickable: false,
                zIndex: 1000,
            })
        },
        stopMeasuring() {
            if (this.measure.measureLines.length > 0) {
                const lastLine = this.measure.measureLines.pop()
                toRaw(lastLine).setMap(null)
            }

            if (this.measure.measureLabels.length > 0) {
                const lastLabel = this.measure.measureLabels.pop()
                toRaw(lastLabel).setMap(null)
            }

            if (this.measure.measureMode === 'area') {
                if (this.measure.measurePolygonPath.length >= 3) {
                    this.finishDrawingArea()
                }
            }

            this.measure.measuring = false
            this.measure.measureMode = null

            this.map.setOptions({ draggableCursor: null })
            this.measureListeners.forEach(listener => {
                this.google.maps.event.removeListener(listener)
            })
            this.measureListeners = []

            if (this.mapClickListener) {
                this.google.maps.event.removeListener(this.mapClickListener)
                this.mapClickListener = null
            }

            this.measureBtnState = 'clear'
            MapDistanceControl.measureButtonText

            // Restore map click listeners that were active before measuring
            MAP_CLICK_LISTENER_STORES.forEach(getStore => getStore().resumeMapClickListener())

            this.setFieldsClickable()
            this.setUncoveredPolygonsClickable()
            this.syncMapOverlaysAfterMeasure()

            if (!this.drawingStore.showSelectBorder) {
                this.enableDrawedField()
            }
        },
        clearMeasuring(buttonContainer) {
            // Reset measurement state
            this.measure.measuring = false
            this.measure.measureMode = null

            // Restore default cursor
            this.map.setOptions({ draggableCursor: null })

            // Remove event listeners to stop measurement interactions
            this.measureListeners.forEach(listener => {
                this.google.maps.event.removeListener(listener)
            })
            this.measureListeners = []

            if (this.mapClickListener) {
                this.google.maps.event.removeListener(this.mapClickListener)
                this.mapClickListener = null
            }

            // Remove measurement polygon if it exists
            if (this.measure.measurePolygon) {
                toRaw(this.measure.measurePolygon).setMap(null)
                this.measure.measurePolygon = null
            }

            // Clear stored measurement data
            this.clearMeasureData()

            if (!this.drawingStore.showSelectBorder) {
                this.enableDrawedField()
            }

            // Restore the dropdown menu for measurement selection
            if (buttonContainer) {
                buttonContainer.innerHTML = ''
            }
            if (this.controlMeasure) {
                this.controlMeasure.innerHTML = ''
            }

            this.measureBtnState = null
            MapDistanceControl.measureButtonText

            // Restore map click listeners that were active before measuring
            MAP_CLICK_LISTENER_STORES.forEach(getStore => getStore().resumeMapClickListener())

            if (!document.getElementById('measureBtn')) {
                this.addMapDistanceControl() // Only add if it's not already present
            }
            this.mapClickListener = null
        },
        finishDrawingArea() {
            // Close the polygon by adding the first point to the end
            this.measure.measurePolygonPath.push(this.measure.measurePolygonPath[0])
            this.measure.measurePolygon.setPath(this.measure.measurePolygonPath)

            // Calculate and display the area
            const coordinates = this.measure.measurePolygonPath.map(point => [point.lng(), point.lat()])
            const polygon = turf.polygon([coordinates])
            const area = turf.area(polygon)

            // Remove existing area label if it exists
            if (this.measure.measureLabels?.length > 0) {
                this.measure.measureLabels[0].setMap(null)
                this.measure.measureLabels = []
            }

            // Create new area label
            const center = turf.center(polygon)
            const labelPosition = new this.google.maps.LatLng(
                center.geometry.coordinates[1],
                center.geometry.coordinates[0]
            )

            const content = document.createElement('div')
            content.style.color = 'white'
            content.style.fontSize = '16px'
            content.style.fontWeight = 'bold'
            content.style.textShadow = '0 0 5px #000000'
            content.innerText = this.formatArea(area)

            const areaLabel = new this.google.maps.marker.AdvancedMarkerElement({
                position: labelPosition,
                map: toRaw(this.map),
                content: content
            })

            this.measure.measureLabels.push(areaLabel)

            // Stop drawing, keeping the stop button
            this.map.setOptions({ draggableCursor: null })

            return
        },
        addAreaPoint(event) {
            if (!this.measure.measuring || this.measure.measureMode !== 'area') return
            if (event.domEvent && event.domEvent.type === 'dblclick' && this.measure.measurePolygonPath.length >= 3) {
                this.stopMeasuring()
            }

            const point = event.latLng

            // Add point to path for normal click
            this.measure.measurePolygonPath.push(point)

            // Update polygon
            this.measure.measurePolygon.setPath(this.measure.measurePolygonPath)

            // If we have at least 3 points, calculate and display area
            if (this.measure.measurePolygonPath.length >= 3) {
                const coordinates = this.measure.measurePolygonPath.map(point => [point.lng(), point.lat()])
                coordinates.push(coordinates[0]) // Close the polygon

                const polygon = turf.polygon([coordinates])
                const area = turf.area(polygon)

                // Remove existing area label if it exists
                if (this.measure.measureLabels.length > 0) {
                    this.measure.measureLabels[0].setMap(null)
                    this.measure.measureLabels = []
                }

                // Create new area label
                const center = turf.center(polygon)
                const labelPosition = new this.google.maps.LatLng(
                    center.geometry.coordinates[1],
                    center.geometry.coordinates[0]
                )

                const content = document.createElement('div')
                content.style.color = 'white'
                content.style.fontSize = '16px'
                content.style.fontWeight = 'bold'
                content.style.textShadow = '0 0 5px #000000'
                content.innerText = this.formatArea(area)

                const areaLabel = new this.google.maps.marker.AdvancedMarkerElement({
                    position: labelPosition,
                    map: toRaw(this.map),
                    content: content
                })

                this.measure.measureLabels.push(areaLabel)
            }
        },
        formatArea(area) {
            const imperialUnitsStore = useImperialUnitsStore()

            if (typeof area !== 'number' || isNaN(area)) {
                throw new TypeError('Parameter "area" must be a valid number.')
            }

            const decimalPart = area.toFixed(10).split('.')[1] || ''

            const digits = decimalPart.split('')
            const firstNonZero = digits.findIndex(d => d !== '0')
            const leadingZeros = firstNonZero === -1 ? 0 : firstNonZero

            const precision = Math.min(Math.max(leadingZeros + 1, 2), 10)
            return `${imperialUnitsStore.formatAreaHectaresAcres(area.toFixed(precision))}`
        },
        updateAreaPreview(event) {
            if (!this.measure.measuring || this.measure.measureMode !== 'area' || this.measure.measurePolygonPath.length === 0) return

            const previewPath = [...this.measure.measurePolygonPath, event.latLng]
            this.measure.measurePolygon.setPath(previewPath)
        },
        startMeasure(event) {
            if (!this.measure.measuring) return
            if (this.measure.measureStart) {
                const measureEnd = event.latLng
                this.addMeasureLine(this.measure.measureStart, measureEnd)
                this.addMeasureLabel(measureEnd)
                this.measure.measureStart = measureEnd
            } else {
                this.measure.measureStart = event.latLng
            }
        },
        updateMeasure(event) {
            if (!this.measure.measuring || !this.measure.measureStart) return
            const measureEnd = event.latLng
            this.updateMeasureLine(measureEnd)
            this.updateMeasureLabel(measureEnd)

            if (this.measure.measuring) {
                this.measure.lastMousePosition = measureEnd
            }
        },
        clearMeasureData() {
            this.measure.measureStart = null
            this.measure.lastMousePosition = null
            this.measure.measureLines.forEach((line) => toRaw(line).setMap(null))
            this.measure.measureLabels.forEach((label) => toRaw(label).setMap(null))
            this.measure.measureLines = []
            this.measure.measureLabels = []

            this.drawingStore.transformToClickableField()
            this.setFieldsClickable()
            this.setUncoveredPolygonsClickable()
            this.syncMapOverlaysAfterMeasure()
        },
        addMeasureLine(start, end) {
            const measureLine = new this.google.maps.Polyline({
                path: [start, end],
                strokeColor: '#000000',
                strokeOpacity: 1.0,
                strokeWeight: 2,
                clickable: false,
                map: this.map,
                zIndex: 1000
            })
            this.measure.measureLines.push(measureLine)
        },
        addMeasureLabel(position) {
            const totalDistance = this.calculateTotalDistance()
            const content = this.createMeasureLabelContent(totalDistance)
            const measureLabel = new this.google.maps.marker.AdvancedMarkerElement({
                position: position,
                map: toRaw(this.map),
                content: content,
                zIndex: 1000
            })
            this.measure.measureLabels.push(measureLabel)
        },
        updateMeasureLine(end) {
            if (this.measure.measureLines.length > 0) {
                const lastLine = this.measure.measureLines[this.measure.measureLines.length - 1]
                lastLine.setPath([this.measure.measureStart, end])
            } else {
                this.addMeasureLine(this.measure.measureStart, end)
            }
        },
        updateMeasureLabel(position) {
            const imperialUnitsStore = useImperialUnitsStore()

            const totalDistance = this.calculateTotalDistance()
            const newPosition = this.adjustLabelPosition(position)
            if (this.measure.measureLabels.length > 0) {
                const lastLabel = this.measure.measureLabels[this.measure.measureLabels.length - 1]
                lastLabel.content.innerText = `${imperialUnitsStore.formatDistanceMetersFeet(totalDistance.toFixed(2))}`
                lastLabel.position = newPosition
            } else {
                this.addMeasureLabel(newPosition)
            }
        },
        calculateTotalDistance() {
            return this.measure.measureLines.reduce((acc, line) => {
                const path = line.getPath()
                return acc + this.google.maps.geometry.spherical.computeDistanceBetween(path.getAt(0), path.getAt(1))
            }, 0)
        },
        createMeasureLabelContent(distance) {
            const imperialUnitsStore = useImperialUnitsStore()

            const content = document.createElement('div')
            content.style.color = 'white'
            content.style.fontSize = '16px'
            content.style.fontWeight = 'bold'
            content.style.textShadow = '0 0 5px #000000'
            content.style.marginBottom = '12px'
            content.innerText = `${imperialUnitsStore.formatDistanceMetersFeet(distance.toFixed(2))}`
            return content
        },
        adjustLabelPosition(position) {
            if (this.measure.lastMousePosition) {
                const dx = position.lng() - this.measure.lastMousePosition.lng()
                const dy = position.lat() - this.measure.lastMousePosition.lat()
                const offset = 0.000001
                return new this.google.maps.LatLng(
                    position.lat() - Math.sign(dy) * offset,
                    position.lng() - Math.sign(dx) * offset
                )
            }
            return position
        },
        /**
         * This function is used to disable the drawed field and
         * make possible to draw the measure lines in the field
         */
        disableDrawedField() {
            if (this.drawingStore.field.shapes.length > 0) {
                this.drawingStore.field.shapes.forEach((shape) => {
                    shape.setOptions({
                        fillColor: 'transparent',
                        fillOpacity: 0,
                        strokeColor: 'red',
                        strokeWeight: 2,
                        zIndex: 0,
                        clickable: false,
                        editable: false
                    })
                })
            }

            if (this.drawingStore.field.cutouts.length > 0) {
                this.drawingStore.field.cutouts.forEach((cutout) => {
                    cutout.setOptions({
                        fillColor: 'transparent',
                        fillOpacity: 0,
                        strokeColor: 'red',
                        strokeWeight: 2,
                        zIndex: 0,
                        clickable: false,
                        editable: false
                    })
                })
            }

            this.mapStore.map.data.setStyle((feature) => {
                const fieldType = feature.getProperty('field_geom_type')
                const counter = feature.getProperty('counter')
                if (fieldType === 'inner_border' && counter === null) {
                    return {
                        fillColor: 'transparent',
                        fillOpacity: 0,
                        strokeColor: 'purple',
                        zIndex: 0,
                        clickable: false,
                        editable: false
                    }
                } else if (feature.getGeometry().getType() === 'MultiLineString') {
                    return {
                        fillOpacity: 0,
                        strokeColor: "#00ff00",
                        zIndex: 0,
                        clickable: false,
                        editable: false
                    }
                } else if (fieldType === 'headland') {
                    return {
                        fillColor: 'transparent',
                        fillOpacity: 0,
                        strokeColor: "#FFFF00",
                        zIndex: 0,
                        clickable: false,
                        editable: false
                    }
                }
            })
        },
        /**
         * This function is used to enable the drawed field and
         * make possible to draw the measure lines in the field
         */
        enableDrawedField() {
            if (this.drawingStore.field.shapes.length > 0) {
                this.drawingStore.field.shapes.forEach((shape) => {
                    shape.setOptions({ clickable: true, editable: true })
                })
                this.drawingStore.setFieldStyle()
            }

            if (this.drawingStore.field.cutouts.length > 0) {
                this.drawingStore.field.cutouts.forEach((cutout) => {
                    cutout.setOptions({ clickable: true, editable: true, zIndex: 999 })
                })
            }

            // Undo disableDrawedField's map.data.setStyle so TerraDraw (and others) can restyle
            this.mapStore.map.data.setStyle(null)
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useMeasure, import.meta.hot))
}
